import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { initialFormData, passwordRequirements } from "./data";
import {
  RegisterFormProps,
  RegisterFormData,
  RegisterFormState,
} from "./types";
import { submitRegistrationForm } from "./forms.action";
import { useAuth } from "@/core/components/Auth/AuthContext";

const RegisterForm: React.FC<RegisterFormProps> = ({
  onSuccess,
  initialData,
}) => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState<RegisterFormData>({
    ...initialFormData,
    ...initialData,
  });

  const [formState, setFormState] = useState<RegisterFormState>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterFormData, string>>
  >({});
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  // Calculate password strength
  const getPasswordStrength = () => {
    if (!formData.password) return 0;

    let strength = 0;
    passwordRequirements.forEach((req) => {
      if (req.regex.test(formData.password)) strength++;
    });

    return (strength / passwordRequirements.length) * 100;
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof RegisterFormData, string>> = {};

    // Validate name fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    // Validate password confirmation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Validate phone
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+?[\d\s()-]{10,}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // Validate date of birth
    if (!formData.birthDate) {
      newErrors.birthDate = "Date of birth is required";
    } else {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 21) {
        newErrors.birthDate = "You must be 21 or older to register";
      }
    }

    // Validate terms agreement
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms =
        "You must agree to the Terms of Service and Privacy Policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear the error for this field when it changes
    if (errors[name as keyof RegisterFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset form state
    setFormState({
      isSubmitting: false,
      isSuccess: false,
      isError: false,
    });

    if (!validateForm()) {
      return;
    }

    setFormState((prev) => ({ ...prev, isSubmitting: true }));

    try {
      const response = await submitRegistrationForm(formData);

      if (response.success) {
        setFormState({
          isSubmitting: false,
          isSuccess: true,
          isError: false,
        });

        // Auto-login after successful registration
        try {
          await login(formData.email, formData.password);

          // Call the onSuccess callback if provided
          if (onSuccess) {
            onSuccess(response);
          } else {
            // Otherwise redirect to account page
            navigate("/account");
          }
        } catch (loginError) {
          console.error("Auto-login failed:", loginError);
          // Still consider registration a success even if auto-login fails
          if (onSuccess) {
            onSuccess(response);
          }
        }
      } else {
        setFormState({
          isSubmitting: false,
          isSuccess: false,
          isError: true,
          errorMessage: response.message,
        });
      }
    } catch (error) {
      setFormState({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: "An unexpected error occurred. Please try again later.",
      });
    }
  };

  const passwordStrength = getPasswordStrength();
  const getPasswordStrengthClass = () => {
    if (passwordStrength <= 25) return "bg-red-500";
    if (passwordStrength <= 50) return "bg-orange-500";
    if (passwordStrength <= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success Message */}
      {formState.isSuccess && (
        <div className="p-4 mb-6 text-sm border-l-4 border-green-500 bg-green-50 text-green-700">
          <div className="font-medium">Account Created Successfully!</div>
          <p>
            Your account has been created. You can now enjoy all the benefits of
            our services.
          </p>
        </div>
      )}

      {/* Error Message */}
      {formState.isError && (
        <div className="p-4 mb-6 text-sm border-l-4 border-red-500 bg-red-50 text-red-700">
          <div className="font-medium">
            There was a problem creating your account
          </div>
          <p>{formState.errorMessage || "Please try again later."}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div>
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            First Name *
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
              errors.firstName ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="John"
            disabled={formState.isSubmitting || formState.isSuccess}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Last Name *
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
              errors.lastName ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Doe"
            disabled={formState.isSubmitting || formState.isSuccess}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
            errors.email ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="john.doe@example.com"
          disabled={formState.isSubmitting || formState.isSuccess}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Phone */}
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Phone Number *
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
            errors.phone ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="(202) 555-1234"
          disabled={formState.isSubmitting || formState.isSuccess}
        />
        {errors.phone && (
          <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
        )}
      </div>

      {/* Date of Birth */}
      <div>
        <label
          htmlFor="birthDate"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Date of Birth * (Must be 21+)
        </label>
        <input
          type="date"
          id="birthDate"
          name="birthDate"
          value={formData.birthDate}
          onChange={handleChange}
          max={
            new Date(new Date().setFullYear(new Date().getFullYear() - 21))
              .toISOString()
              .split("T")[0]
          }
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
            errors.birthDate ? "border-red-500" : "border-gray-300"
          }`}
          disabled={formState.isSubmitting || formState.isSuccess}
        />
        {errors.birthDate && (
          <p className="mt-1 text-sm text-red-600">{errors.birthDate}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          You must be 21 or older to register. Your age will be verified.
        </p>
      </div>

      {/* Password */}
      <div>
        <label
          htmlFor="password"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Password *
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          onFocus={() => setShowPasswordRequirements(true)}
          onBlur={() => setShowPasswordRequirements(false)}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
            errors.password ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Create a strong password"
          disabled={formState.isSubmitting || formState.isSuccess}
        />
        {errors.password && (
          <p className="mt-1 text-sm text-red-600">{errors.password}</p>
        )}

        {/* Password strength indicator */}
        {formData.password && (
          <div className="mt-2">
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${getPasswordStrengthClass()} transition-all duration-300`}
                style={{ width: `${passwordStrength}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Password strength:{" "}
              {passwordStrength <= 25
                ? "Weak"
                : passwordStrength <= 50
                ? "Fair"
                : passwordStrength <= 75
                ? "Good"
                : "Strong"}
            </p>
          </div>
        )}

        {/* Password requirements */}
        {showPasswordRequirements && (
          <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded text-sm">
            <p className="font-medium text-gray-700 mb-1">
              Password requirements:
            </p>
            <ul className="space-y-1 text-gray-600">
              {passwordRequirements.map((req, index) => (
                <li key={index} className="flex items-center">
                  {req.regex.test(formData.password) ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-green-500 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-400 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {req.text}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Confirm Password *
        </label>
        <input
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 transition-colors ${
            errors.confirmPassword ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Confirm your password"
          disabled={formState.isSubmitting || formState.isSuccess}
        />
        {errors.confirmPassword && (
          <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
        )}
      </div>

      {/* Communication Preferences */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-sm font-semibold mb-3 text-gray-700">
          Communication Preferences
        </h3>

        <div className="space-y-3">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="eventUpdates"
                name="eventUpdates"
                type="checkbox"
                checked={formData.eventUpdates}
                onChange={handleChange}
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-cannabis-green-300"
                disabled={formState.isSubmitting || formState.isSuccess}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="eventUpdates" className="text-gray-700">
                Event Updates
              </label>
              <p className="text-gray-500">
                Get notified about upcoming events and community gatherings.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="promoOffers"
                name="promoOffers"
                type="checkbox"
                checked={formData.promoOffers}
                onChange={handleChange}
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-cannabis-green-300"
                disabled={formState.isSubmitting || formState.isSuccess}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="promoOffers" className="text-gray-700">
                Promotional Offers
              </label>
              <p className="text-gray-500">
                Receive emails about special deals and promotions.
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="textMessages"
                name="textMessages"
                type="checkbox"
                checked={formData.textMessages}
                onChange={handleChange}
                className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-cannabis-green-300"
                disabled={formState.isSubmitting || formState.isSuccess}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="textMessages" className="text-gray-700">
                Text Messages
              </label>
              <p className="text-gray-500">
                Receive SMS notifications for important updates and offers.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div>
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="agreeToTerms"
              name="agreeToTerms"
              type="checkbox"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              className={`w-4 h-4 border rounded bg-gray-50 focus:ring-3 focus:ring-cannabis-green-300 ${
                errors.agreeToTerms ? "border-red-500" : "border-gray-300"
              }`}
              disabled={formState.isSubmitting || formState.isSuccess}
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="agreeToTerms" className="text-gray-700">
              I agree to the{" "}
              <Link
                to="/terms-of-service"
                className="text-cannabis-green-600 hover:underline"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy-policy"
                className="text-cannabis-green-600 hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </label>
          </div>
        </div>
        {errors.agreeToTerms && (
          <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms}</p>
        )}
      </div>

      {/* Submit Button */}
      <div>
        <button
          type="submit"
          className="w-full px-8 py-3 bg-cannabis-green-500 hover:bg-cannabis-green-600 text-white font-medium rounded-lg transition duration-300 flex items-center justify-center"
          disabled={formState.isSubmitting || formState.isSuccess}
        >
          {formState.isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Creating Account...
            </>
          ) : formState.isSuccess ? (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
              Account Created
            </>
          ) : (
            "Create Account"
          )}
        </button>
      </div>

      {/* Already have an account */}
      <div className="text-center mt-4">
        <p className="text-sm text-gray-600">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-cannabis-green-600 hover:text-cannabis-green-500 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </form>
  );
};

export default RegisterForm;
